/* global API, Site */

/**
 * composables -kansiossa olevat tiedostot on tarkoituksella pilkottu päätiedostoista (search.js, services.js etc.)
 * Tämä johtuu siitä, että näissä tiedostoissa olevia datoja tai funktioita käytetään tismalleen samanlaisena eri
 * komponenteissa, jos niihin jostain syystä tarvitsee tehdä muutoksia, ei niitä tarvitse muistaa tehdä useaan paikkaan
 *
 * Vuen mukaan composablejen prefixinä tulisi käyttää use-sanaa (useFunktionNimi)
 *
 * Dataobjektit
 */

const TAXONOMY_MAPPINGS = {
	alueet: 'al',
	julkaisumuodot: 'jm',
	julkisuus: 'jk',
	kayttotarkoitukset: 'kt',
	kielet: 'kl',
	elioryhma: 'er',
	greencomp: 'gc',
	laaja_oppiaine_query: 'loq',
	laaja_tavoite_query: 'ltq',
	laaja_vuosiluokka_query: 'lvq',
	luokitukset: 'lu',
	maksullisuus: 'ma',
	materiaalin_kohderyhmat: 'mk',
	palvelun_kohderyhmat: 'pk',
	palvelun_luokitukset: 'pl',
	soveltuvuudet: 'so',
	tapahtuman_kategoriat: 'tk',
	tapahtuman_kohderyhmat: 'tk2',
	teemarepun_kohderyhmat: 'tk3',
	verkostot: 've',
	subject: 'su',
	subject_class: 'sc',
	subject_target: 'st',
	subject_lang: 'sl',
	subject_area: 'sa',
};

/**
 * Convert decimal to hex
 * @param {number} dec Decimal number
 * @returns {string} Hexadecimal number
 */
export function dec2hex(dec) {
	return dec.toString(16).padStart(2, '0');
}

/**
 * Create session ID
 * @returns {string} Session ID
 */
export function createSessionID() {
	const array = new Uint8Array(16);

	crypto.getRandomValues(array);

	return Array.from(array, dec2hex).join('');
}

/**
 * Queryn data
 */
export const useQuery = {
	session_id: createSessionID(),
	language: Site.lang_slug,
	log: false,
	order: '',
	orderby: '',
	meta_key: '',
	user_page: false,
	status: 'publish',
	posts_per_page: 10,
	short: true,
	taxonomies: false,
	and: false,
	laajahaku: false,
	is_initial: true,
	meta_query: {
		hakusana: '',
		publisher: '',
		start_date: null,
		end_date: null,
		finnish_only: false,
		swedish_only: false,
		english_only: false,
		lyke_only: false,
	},
	client_filters: {
		service_terms: [],
	},
	tax_query: {
		alueet: '',
		julkaisumuodot: '',
		julkisuus: '',
		kayttotarkoitukset: '',
		kielet: '',
		elioryhma: '',
		materiaalin_kohderyhmat: '',
		teemarepun_kohderyhmat: '',
		tapahtuman_kohderyhmat: '',
		palvelun_kohderyhmat: '',
		palvelun_luokitukset: [],
		soveltuvuudet: '',
		tapahtuman_kategoriat: '',
		luokitukset: '',
		maksullisuus: [],
		greencomp: '',
		verkostot: '',
		laaja_tavoite_query: '',
		laaja_oppiaine_query: '',
		laaja_vuosiluokka_query: '',
		laaja_tavoite: '',
		laaja_oppiaine: '',
		laaja_vuosiluokka: '',
	},
};

/**
 * Tarkistaa näytetäänkö latausspinneri
 * @returns {boolean} true jos fetchit eivät ole valmiita
 */
export function useLoading() {
	let isLoading = false;
	const fetches = new Map(Object.entries(this.fetchesLoading));

	fetches.forEach((fetch) => {
		if (fetch) {
			isLoading = true;
		}
	});

	return isLoading;
}

/**
 * Poistaa postin
 * @param {object} post Postiobjekti
 */
export function useDelete(post) {
	const lang = Site.lang === 'sv-SE' ? 'sv' : 'fi';

	const title = post[lang]?.title ? post[lang].title : post.fi.title;

	const conf = confirm(`Olet poistamassa kohdetta ${title}. Oletko varma?`);

	if (conf) {
		fetch(`${API.url}/delete`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json; charset=utf-8',
				'X-WP-Nonce': API.nonce,
			},
			body: JSON.stringify({
				post_id: post.post_id,
			}),
		})
			.then((res) => res.json())
			.then((response) => {
				alert(response.message);

				this.search();
			})
			.catch((error) => console.error('Error', error));
	}
}

/**
 * Capitalize the first letter of a string
 * @param {string} string String to capitalize
 * @returns {string} Capitalized string
 */
export function useUcFirst(string) {
	if (!string) {
		return string;
	}

	return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * Get the title for the element
 * @param {object} element Element
 * @param {*} that Vue 'this' object
 * @returns {string} Title
 */
export function useGetTitle(element, that = false) {
	if (element.post_type === 'lajikortit') {
		// Lajikortit has titles in 'names' array
		if (element.names[this.lang]) {
			return useUcFirst(element.names[this.lang]);
		}

		if (element.names.fi) {
			return useUcFirst(element.names.fi);
		}

		if (element.names.en) {
			return useUcFirst(element.names.en);
		}

		if (element.names.sv) {
			return useUcFirst(element.names.sv);
		}

		// Lajikortit also has the 'sc' (scientific name) field
		if (element.names.sc) {
			return useUcFirst(element.names.sc);
		}
	} else {
		// Should have commented this, no idea what it does at this point, gonna leave it here for now
		if (that) {
			if (element[that.lang]?.title) {
				return element[that.lang].title;
			}
		} else if (element[this.lang]?.title) {
			return element[this.lang].title;
		}

		if (element.fi?.title) {
			return element.fi.title;
		}

		if (element.en?.title) {
			return element.en.title;
		}

		if (element.sv?.title) {
			return element.sv.title;
		}
	}

	return I18n.noTitle;
}

/**
 * Check if the element has a short description
 * @param {object} element Element
 * @returns {boolean} True if element has a short description
 */
export function useHasShortDesc(element) {
	if (element[this.lang]?.short_desc) {
		return true;
	}

	if (element[this.lang]?.description) {
		return false;
	}

	if (element.fi?.short_desc) {
		return true;
	}

	if (element.fi?.description) {
		return false;
	}

	if (element.en?.short_desc) {
		return true;
	}

	if (element.en?.description) {
		return false;
	}

	return false;
}

/**
 * Get the short description for the element
 * @param {object} element Element
 * @returns {string} Short description
 */
export function useGetShortDesc(element) {
	if (element[this.lang]?.short_desc) {
		return element[this.lang].short_desc;
	}

	if (element.fi?.short_desc) {
		return element.fi.short_desc;
	}

	if (element.en?.short_desc) {
		return element.en.short_desc;
	}

	if (element.sv?.short_desc) {
		return element.sv.short_desc;
	}

	return '';
}

/**
 * Get the description for the element
 * @param {object} element Element
 * @returns {string} Description
 */
export function useGetDesc(element) {
	if (element.post_type === 'lajikortit') {
		// Lajikortit has titles in 'descriptions' array
		if (element.descriptions[this.lang]) {
			return element.descriptions[this.lang];
		}

		if (element.descriptions?.fi) {
			return element.descriptions.fi;
		}

		if (element.descriptions?.en) {
			return element.descriptions.en;
		}

		if (element.descriptions?.sv) {
			return element.descriptions.sv;
		}
	} else {
		if (element[this.lang]?.description) {
			return element[this.lang].description;
		}

		if (element.fi?.description) {
			return element.fi.description;
		}

		if (element.en?.description) {
			return element.en.description;
		}

		if (element.sv?.description) {
			return element.sv.description;
		}
	}

	return '';
}

/**
 * Get the term counts
 * @param {Array} args Arguments
 * @param {boolean} useRelevanssi Whether to use Relevanssi
 */
export function useGetTermCounts(args, useRelevanssi) {
	const body = {
		args,
		use_relevanssi: useRelevanssi,
	};

	fetch(`${API.url}/get_term_counts`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json; charset=utf-8',
			'X-WP-Nonce': API.nonce,
		},
		body: JSON.stringify(body),
	})
		.then((res) => res.json())
		.then((response) => {
			this.termCounts = response;
		})
		.catch((error) => console.error('Error', error));
}

/**
 * Get the count for a term
 * @param {string} taxonomy Taxonomy
 * @param {number} termId Term ID
 * @returns {string} Count
 */
export function useGetCount(taxonomy, termId) {
	let count = this.termCounts?.[taxonomy]?.[termId];

	if (count === undefined) {
		count = 0;
	}

	const kpl = I18n.unitText;

	return count >= 100 ? `(100+ ${kpl})` : `(${count} ${kpl})`;
}

/**
 * Get the referrer for the search
 * @returns {string} Query string
 */
export function useGetReferrer() {
	const initialParams = new URLSearchParams(window.location.search);

	let queryString;

	// Ollaanko tultu yksittäisen sisällön sivulta? Takaisin-napissa on linkissä ?referrer={post_type}
	if (initialParams.has('referrer')) {
		// Haetaan localstoragessa hakustringi
		const referrer = initialParams.get('referrer');
		const selector = `${referrer}QueryString`;

		let storageOptions = localStorage.getItem(selector);

		if (storageOptions) {
			storageOptions = JSON.parse(storageOptions);

			const { expires } = storageOptions;

			const date = new Date();

			if (expires && expires > date.getTime()) {
				queryString = storageOptions.queryString;
			} else {
				localStorage.removeItem(selector);
			}
		}
	}

	return queryString || window.location.search;
}

/**
 * Convert params to nice fromat
 * @param {object} object Object
 * @returns {object} Nice format object
 */
export function useNiceFormat(object) {
	const niceMetaQuery = {};

	if (object.meta_query) {
		// Shorten the key's to make the URL shorter
		if (object.meta_query.end_date) {
			niceMetaQuery.ed = object.meta_query.end_date;
		}

		if (object.meta_query.start_date) {
			niceMetaQuery.sd = object.meta_query.start_date;
		}

		if (object.meta_query.hakusana) {
			niceMetaQuery.hs = object.meta_query.hakusana;
		}

		if (object.meta_query.publisher) {
			niceMetaQuery.pub = object.meta_query.publisher;
		}

		if (object.meta_query.english_only) {
			niceMetaQuery.en = object.meta_query.english_only;
		}

		if (object.meta_query.finnish_only) {
			niceMetaQuery.fi = object.meta_query.finnish_only;
		}

		if (object.meta_query.swedish_only) {
			niceMetaQuery.sv = object.meta_query.swedish_only;
		}
	}

	const niceTaxQuery = {};

	if (object.tax_query) {
		Object.entries(object.tax_query).forEach(([key, value]) => {
			const hasValue = Array.isArray(value) ? value.length > 0 : value;

			if (hasValue && TAXONOMY_MAPPINGS[key]) {
				niceTaxQuery[TAXONOMY_MAPPINGS[key]] = value;
			}
		});
	}

	const niceClientFilters = {};

	if (object.client_filters) {
		if (object.client_filters.service_terms && object.client_filters.service_terms.length > 0) {
			niceClientFilters.st = object.client_filters.service_terms;
		}
	}

	let niceMetaQueryBase64 = false;

	if (Object.keys(niceMetaQuery).length > 0) {
		niceMetaQueryBase64 = btoa(JSON.stringify(niceMetaQuery));
	}

	let niceTaxQueryBase64 = false;

	if (Object.keys(niceTaxQuery).length > 0) {
		niceTaxQueryBase64 = btoa(JSON.stringify(niceTaxQuery));
	}

	let niceClientFiltersBase64 = false;

	if (Object.keys(niceClientFilters).length > 0) {
		niceClientFiltersBase64 = btoa(JSON.stringify(niceClientFilters));
	}

	return {
		meta_query: niceMetaQueryBase64,
		tax_query: niceTaxQueryBase64,
		client_filters: niceClientFiltersBase64,
	};
}

/**
 * Map the short query param to long one
 * @param {string} short Short query param
 * @param {string} type Type of the query param, meta or tax
 * @returns {string} Long query param
 */
export function useMapShortQuery(short, type) {
	if (type === 'meta') {
		const map = {
			ed: 'end_date',
			fi: 'finnish_only',
			hs: 'hakusana',
			pub: 'publisher',
			sd: 'start_date',
			sv: 'swedish_only',
			en: 'english_only',
		};

		return map[short];
	}

	if (type === 'tax') {
		// Convert the mapping object to a reverse lookup
		const reverseMap = Object.entries(TAXONOMY_MAPPINGS).reduce((acc, [long, shortCode]) => {
			acc[shortCode] = long;
			return acc;
		}, {});

		return reverseMap[short] || '';
	}

	if (type === 'client') {
		const map = {
			st: 'service_terms',
		};

		return map[short];
	}

	return '';
}

/**
 * Create params for the search
 * @param {string} postType Post type
 * @param {object} qobject Query object
 * @param {boolean} base Base URL, default is current URL
 * @param {boolean} append Append the query params to the URL, default is true
 * @param {boolean} ignoreStorage Ignore the storage, default is false
 * @returns {URL} URL object
 */
export function useCreateParams(postType, qobject, base = false, append = true, ignoreStorage = false) {
	const queryObject = JSON.parse(JSON.stringify(qobject));

	if (!base) {
		base = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
	}

	const url = new URL(base);

	delete queryObject.tax_query.laaja_tavoite;
	delete queryObject.tax_query.laaja_oppiaine;
	delete queryObject.tax_query.laaja_vuosiluokka;
	delete queryObject.tax_query.soveltuvuudet;

	if (queryObject.tab) {
		url.searchParams.append('tab', queryObject.tab);
	}

	if (queryObject.forcelog) {
		url.searchParams.append('forcelog', queryObject.forcelog);
	}

	if (queryObject.postType) {
		url.searchParams.append('postType', queryObject.postType);
	}

	if (queryObject.postsPerPage) {
		url.searchParams.append('postsPerPage', queryObject.postsPerPage);
	}

	if (this?.helpers?.subjectObject) {
		const subjectMappings = {
			subject: 'subject',
			subject_class: 'subject_class',
			subject_target: 'subject_target',
			subject_lang: 'subject_lang',
			subject_area: 'subject_area',
		};

		Object.entries(subjectMappings).forEach(([property, taxKey]) => {
			if (this.helpers.subjectObject[property]) {
				queryObject.tax_query[taxKey] = this.helpers.subjectObject[property].term_id;
			}
		});
	}

	const niceParams = useNiceFormat(queryObject);

	if (niceParams.meta_query) {
		url.searchParams.append('meta_query', niceParams.meta_query);
	}

	if (niceParams.tax_query) {
		url.searchParams.append('tax_query', niceParams.tax_query);
	}

	if (niceParams.client_filters) {
		url.searchParams.append('client_filters', niceParams.client_filters);
	}

	const date = new Date();

	date.setMinutes(date.getMinutes() + 10);

	if (!ignoreStorage) {
		const storageArgs = {
			queryString: url.searchParams.toString(),
			expires: date.getTime(),
		};

		const selector = `${postType}QueryString`;

		localStorage.setItem(selector, JSON.stringify(storageArgs));
	}

	if (append) {
		window.history.replaceState(null, null, url);
	}

	return url;
}

/**
 * Get OPS from the API
 * @param {boolean} getLaajas Whether to get laajas also
 * @returns {Promise} Promise object
 */
export async function useGetOPS(getLaajas = false) {
	this.helpers.loading = true;

	const url = `${API.url}/ops/get`;

	return fetch(url, {
		method: 'POST',
		headers: {
			'X-WP-Nonce': API.nonce,
		},
	})
		.then((res) => res.json())
		.then((response) => {
			this.data.ops = response;

			this.helpers.loading = false;

			if (this.showInfoModal) {
				this.openOPS(this.id, this.termDescription);
			}

			if (getLaajas) {
				const map = new Map(Object.entries(response));

				const laajas = [];

				map.forEach((item) => {
					if (item.laajattavoitteet) {
						item.laajattavoitteet.forEach((laaja) => {
							laajas.push(laaja);
						});
					}
				});

				laajas.forEach((laaja) => {
					if (JSON.stringify(this.data.laajas).indexOf(JSON.stringify(laaja)) === -1) {
						this.data.laajas.push(laaja);
					}
				});

				this.data.laajas.sort((a, b) => {
					const aName = a.nimi?.[this.lang] ? a.nimi[this.lang] : '';
					const bName = b.nimi?.[this.lang] ? b.nimi[this.lang] : '';

					return aName.localeCompare(bName);
				});
			}
		})
		.catch((error) => {
			console.error('Error', error);
		});
}

/**
 * Get correct href for links
 * @param {string} lang Language code
 * @param {Array} links Links array
 * @param {string} selector Selector, default is 'permalink'
 * @returns {string} Href
 */
export const useGetHref = (lang, links, selector = 'permalink') => {
	if (links[selector]) {
		return links[selector];
	}

	console.error(`No link found for lang ${lang} and selector ${selector}`, links);

	return '';
};

/**
 * Check if the given string is in base64 format
 * @param {string} str String to check
 * @returns {boolean} True if the string is in base64 format
 */
export function useIsBase64(str) {
	try {
		return btoa(atob(str)) === str;
	} catch (err) {
		return false;
	}
}

/**
 * Parse the query param
 * @param {string} param The query param
 * @param {string} type The type of the query param, meta or tax
 * @returns {object} The parsed query param
 */
export function useParseQueryParam(param, type) {
	if (!param) {
		return false;
	}

	if (useIsBase64(param)) {
		const parsedParams = JSON.parse(atob(param));

		// Map the keys to long versions
		const newParams = {};

		Object.keys(parsedParams).forEach((key) => {
			const newKey = useMapShortQuery(key, type);

			if (newKey) {
				newParams[newKey] = parsedParams[key];
			}
		});

		return newParams;
	} else {
		return JSON.parse(param);
	}
}

/**
 * Get the search params from the query URL
 * @param {boolean} triggerSearch Whether to trigger search or not
 */
export function useGetParams(triggerSearch = true) {
	// Otetaan parametrit vuen dataobjektiin ja suoritetaan niiden perusteella haku
	const queryString = useGetReferrer();

	const params = new URLSearchParams(queryString);

	const log = params.has('forcelog') ? params.get('forcelog') : false;
	const tab = params.has('tab') ? params.get('tab') : false;

	if (params.has('postType')) {
		this.postType = params.get('postType');
		this.useQuery.post_type = params.get('postType');
	}

	if (params.has('postsPerPage')) {
		this.useQuery.posts_per_page = params.get('postsPerPage');
	}

	if (params.has('client_filters')) {
		// Client filters is new addition and is stored in base64 format
		const clientFilters = useParseQueryParam(params.get('client_filters'), 'client');

		if (clientFilters) {
			Object.keys(clientFilters).forEach((key) => {
				this.useQuery.client_filters[key] = clientFilters[key];
			});
		}
	}

	if (params.has('meta_query') || params.has('tax_query')) {
		// The old system had queries in JSON format, the new one has them in base64
		// So we need to detect which one is used
		const metaQuery = useParseQueryParam(params.get('meta_query'), 'meta');
		const taxQuery = useParseQueryParam(params.get('tax_query'), 'tax');

		if (metaQuery) {
			// In the new format, meta_query object won't have keys if the value is empty, so we need to map the values rather than overwrite the object
			Object.keys(metaQuery).forEach((key) => {
				this.useQuery.meta_query[key] = metaQuery[key];
			});

			this.helpers.keyword = this.useQuery.meta_query.hakusana || '';
		}

		if (taxQuery) {
			// In the new format, tax_query object won't have keys if the value is empty, so we need to map the values rather than overwrite the object
			Object.keys(taxQuery).forEach((key) => {
				this.useQuery.tax_query[key] = taxQuery[key];
			});

			if (this.useQuery.tax_query.greencomp) {
				const first = this.useGetGcBySlug(this.useQuery.tax_query.greencomp[0], 'parents') || '';
				const second = this.useGetGcBySlug(this.useQuery.tax_query.greencomp[1], 'children') || '';
				const third = this.useGetGcBySlug(this.useQuery.tax_query.greencomp[2], 'grandchildren') || '';

				this.helpers.greencomp = {
					first,
					second,
					third,
				};
			}

			if (this.useQuery.tax_query.laaja_tavoite_query) {
				this.helpers.laajaObject.tavoite = this.useQuery.tax_query.laaja_tavoite_query;
			}

			if (this.useQuery.tax_query.laaja_oppiaine_query) {
				this.helpers.laajaObject.oppiaine = this.useQuery.tax_query.laaja_oppiaine_query;
			}

			if (this.useQuery.tax_query.laaja_vuosiluokka_query) {
				this.helpers.laajaObject.vuosiluokka = this.useQuery.tax_query.laaja_vuosiluokka_query;
			}

			if (this.useQuery.tax_query.subject) {
				const subject = this.results.subjects.find((item) => item.term_id === this.useQuery.tax_query.subject);

				this.helpers.subjectObject.subject = subject ?? '';
			}

			if (this.useQuery.tax_query.subject_class) {
				const subject = this.results.subjects.find((item) => item.term_id === this.useQuery.tax_query.subject_class);

				this.helpers.subjectObject.subject_class = subject ?? '';
			}

			if (this.useQuery.tax_query.subject_target) {
				const subject = this.results.subjects.find((item) => item.term_id === this.useQuery.tax_query.subject_target);

				this.helpers.subjectObject.subject_target = subject ?? '';
			}

			if (this.useQuery.tax_query.subject_lang) {
				const subject = this.results.subjects.find((item) => item.term_id === this.useQuery.tax_query.subject_lang);

				this.helpers.subjectObject.subject_lang = subject ?? '';
			}

			if (this.useQuery.tax_query.subject_area) {
				const subject = this.results.subjects.find((item) => item.term_id === this.useQuery.tax_query.subject_area);

				this.helpers.subjectObject.subject_area = subject ?? '';
			}
		}

		if (tab) {
			this.tab = tab;
			this.changeTab(tab);

			if (tab === 'gc') {
				if (this.helpers.greencomp.first) {
					this.gcSearch();
				}
			} else if (tab === 'ops') {
				if (this.helpers.subjectObject.subject) {
					this.subjectSearch();
				}
			} else if (tab === 'laaja') {
				if (
					this.helpers.laajaObject.tavoite ||
					this.helpers.laajaObject.oppiaine ||
					this.helpers.laajaObject.vuosiluokka
				) {
					this.laajaSearch();
				}
			}
		} else if (triggerSearch) {
			this.search(log);
		}
	} else if (params.has('hakusana')) {
		this.helpers.keyword = params.get('hakusana');

		this.search(log);
	} else if (triggerSearch && !this.redirect) {
		this.search(log);
	}
}

/**
 * Format the date
 * @param {string} date Date
 * @returns {string} Formatted date
 */
export function useFormatDate(date) {
	const formatted = new Date(date);

	return formatted.toLocaleDateString('fi-FI');
}

/**
 * Format the event date with time
 * @param {object} dates Dates object containing startDate and endDate
 * @returns {string} Formatted date with time
 */
export function useFormatEventDateWithTime(dates) {
	const startDate = dates?.startDate || dates?.start_date || null;
	const endDate = dates?.endDate || dates?.end_date || null;

	const pad = (n) => n.toString().padStart(2, '0');

	if (startDate && endDate) {
		const startDateObject = new Date(startDate);
		const endDateObject = new Date(endDate);

		const startUnix = startDateObject.getTime();
		const endUnix = endDateObject.getTime();

		if (startUnix === endUnix) {
			const dateStr = `${pad(startDateObject.getDate())}.${pad(startDateObject.getMonth() + 1)}.${startDateObject.getFullYear()}`;
			const startTime = `${pad(startDateObject.getHours())}:${pad(startDateObject.getMinutes())}`;

			return `${dateStr} ${startTime}`;
		}

		const startDay = startDateObject.getDate();
		const endDay = endDateObject.getDate();

		if (startDay === endDay) {
			// Format: dd.mm.YYYY H:m - H:m
			const dateStr = `${pad(startDateObject.getDate())}.${pad(startDateObject.getMonth() + 1)}.${startDateObject.getFullYear()}`;
			const startTime = `${pad(startDateObject.getHours())}:${pad(startDateObject.getMinutes())}`;
			const endTime = `${pad(endDateObject.getHours())}:${pad(endDateObject.getMinutes())}`;

			return `${dateStr} ${startTime} - ${endTime}`;
		}

		// Format: dd.mm.YYYY H:m - dd.mm.YYYY H:m
		const startDateStr = `${pad(startDateObject.getDate())}.${pad(startDateObject.getMonth() + 1)}.${startDateObject.getFullYear()}`;
		const startTime = `${pad(startDateObject.getHours())}:${pad(startDateObject.getMinutes())}`;
		const endDateStr = `${pad(endDateObject.getDate())}.${pad(endDateObject.getMonth() + 1)}.${endDateObject.getFullYear()}`;
		const endTime = `${pad(endDateObject.getHours())}:${pad(endDateObject.getMinutes())}`;

		return `${startDateStr} ${startTime} - ${endDateStr} ${endTime}`;
	} else if (startDate) {
		const startDateObject = new Date(startDate);

		return startDateObject.toLocaleDateString('fi-FI');
	} else if (endDate) {
		const endDateObject = new Date(endDate);

		return endDateObject.toLocaleDateString('fi-FI');
	}

	return '';
}

/**
 * Format the event date.
 * @param {object} dates - Object containing startDate and/or endDate.
 * @returns {string} Formatted event date string.
 */
export function useFormatEventDate(dates) {
	const startDate = dates?.startDate || dates?.start_date || null;
	const endDate = dates?.endDate || dates?.end_date || null;

	const pad = (n) => n.toString().padStart(2, '0');

	if (startDate && endDate) {
		const startDateObject = new Date(startDate);
		const endDateObject = new Date(endDate);

		const startUnix = startDateObject.getTime();
		const endUnix = endDateObject.getTime();

		if (startUnix === endUnix) {
			// Format: dd.mm.YYYY
			return `${pad(startDateObject.getDate())}.${pad(startDateObject.getMonth() + 1)}.${startDateObject.getFullYear()}`;
		}

		const startDay = startDateObject.getDate();
		const endDay = endDateObject.getDate();
		const startMonth = startDateObject.getMonth();
		const endMonth = endDateObject.getMonth();
		const startYear = startDateObject.getFullYear();
		const endYear = endDateObject.getFullYear();

		if (startDay === endDay && startMonth === endMonth && startYear === endYear) {
			// Same day
			return `${pad(startDateObject.getDate())}.${pad(startDateObject.getMonth() + 1)}.${startDateObject.getFullYear()}`;
		}

		// Different days
		const startDateStr = `${pad(startDateObject.getDate())}.${pad(startDateObject.getMonth() + 1)}.${startDateObject.getFullYear()}`;
		const endDateStr = `${pad(endDateObject.getDate())}.${pad(endDateObject.getMonth() + 1)}.${endDateObject.getFullYear()}`;

		return `${startDateStr} - ${endDateStr}`;
	} else if (startDate) {
		const startDateObject = new Date(startDate);

		return `${pad(startDateObject.getDate())}.${pad(startDateObject.getMonth() + 1)}.${startDateObject.getFullYear()}`;
	} else if (endDate) {
		const endDateObject = new Date(endDate);

		return `${pad(endDateObject.getDate())}.${pad(endDateObject.getMonth() + 1)}.${endDateObject.getFullYear()}`;
	}

	return '';
}

/**
 * Create a URL for the search page
 * @param {string} postType Post type
 * @param {boolean} forcelog Whether to log the search or not
 * @returns {string} URL for the search page
 */
export function useSearchPageUrl(postType, forcelog = false) {
	let base = Permalinks[postType] || `${Site.url}/haku`;

	this.useQuery.forcelog = forcelog;

	const url = useCreateParams(postType, this.useQuery, base, false, true);

	return url;
}

/**
 * Format switcher
 * @param {string} format Format to change to
 */
export function useChangeFormat(format) {
	this.format = format;
}
